import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators'
import { } from 'vue-upload-component'
import { Asset } from '@/rest'
import Vue from 'vue'
import { store } from '@/store'

export interface AssetUsage { }

export interface SingleAssetUsage extends AssetUsage {
    rotation: number
    zoom: number
    center: [number, number]
}

type AssetUsages = {
    [key: string]: AssetUsage
}

export type Rect = [number, number]
export type Point = Rect
export type BoundingBox = {
    ll: Point,
    ur: Point
}

export class AssetFile implements AssetFile {   
    public url: string
    public assetId?: number
    public started: number
    public uploaded: number
    public estimatedProgress: number
    public rect: Rect
    public selected: boolean
    public usage: AssetUsages
    public pendingUsage: AssetUsages

    public constructor (url: string, width: number, height: number) {
        this.url = url
        this.started = 0
        this.uploaded = 0
        this.estimatedProgress = 0
        this.rect = [width,height]
        this.selected = false
        this.usage = {}
        this.pendingUsage = {}
        this.active = false
    }
}

export interface AssetFile extends VUFile {
    url: string
    assetId?: number
    started: number
    uploaded: number
    estimatedProgress: number
    rect: Rect
    selected: boolean
    usage: AssetUsages
    pendingUsage: AssetUsages
}

export interface AssetsState {
    assets: AssetFile[]
    selectedAssets: AssetFile[]
    validAssets: AssetFile[]
}

export function hasUsage(asset: AssetFile): boolean {
    return Object.keys(asset.usage).length > 0
}

export function isSelectable(asset: AssetFile): boolean {
    return asset.assetId !== undefined && asset.rect[0] > 0 && asset.rect[1] > 0
}

@Module({ dynamic: true, store, name: 'assets' })
export class Assets extends VuexModule implements AssetsState {

    public assets: AssetFile[] = []
    public selectedAssets: AssetFile[] = []
    public validAssets: AssetFile[] = []

    @Mutation 
    private SET_ASSETS(assets: AssetFile[]) {
        this.assets = assets
        this.assets.forEach(asset => {
            if (!asset.assetId && !asset.active && asset.success) {
                Vue.set(asset, 'assetId', (asset.response as Asset).id);
            }
            if (isSelectable(asset) && !this.validAssets.some(a => a.id === asset.id)) {
                this.validAssets.splice(0, 0, asset)
            }
            if (asset.uploaded < 1 && asset.progress === '100.00' ) {
                window.console.log('set uploaded for asset')
                Vue.set(asset, 'uploaded', Date.now())
            }
        })
    }

    @Mutation
    private SELECT_ASSET(asset?: AssetFile) {
        this.assets.forEach(currentAsset => {
            let isSelected = asset ? currentAsset.id === asset.id : false
            if (currentAsset.selected !== isSelected) {
                currentAsset.selected = isSelected
            }
        })
        this.selectedAssets = this.assets.filter(a => a.selected)
    }

    @Mutation
    private SET_USAGES(asset: AssetFile, key: string, usages: Array<AssetUsage>) {
        if (!usages || usages.length === 0) {
            delete asset.usage.key
        } else {
            asset.usage[key] = usages
        }
    }

    @Mutation
    private SET_URL([id, url]: [string | number, string]) {
        let asset: AssetFile | undefined
        this.assets.forEach((a) => {
            if (a.id === id) {
                a.url = url
                asset = a
            }
        })
        if (asset !== undefined) {
            if (isSelectable(asset) && !this.validAssets.some(a => a.id === asset!.id)) {
                this.validAssets.push(asset)
            }
        }
    }

    @Mutation
    private SET_RECTANGLE([asset, rect]: [AssetFile, Rect]) {
        asset.rect = rect
        if (isSelectable(asset) && !this.validAssets.some(a => a.id === asset.id)) {
            this.validAssets.push(asset)
        }
    }

    @Mutation
    private CUSTOM_PUZZLE_SET_RECTANGLE([asset, rect]: [AssetFile, Rect]) {
        asset.rect = rect
        if (isSelectable(asset) && !this.validAssets.some(a => a.id === asset.id)) {
            this.validAssets.splice(0, 0, asset)
        }
    }

    @Mutation
    private SET_ESTIMATED_PROGRESS([asset, progress]: [AssetFile, number]) {
        asset.estimatedProgress = progress
    }

    @Mutation
    private SET_PENDING_USAGE([asset, key, usage]: [AssetFile, string, AssetUsage?]) {
        if (usage !== undefined) {
            asset.pendingUsage[key] = usage
        } else {
            delete asset.pendingUsage[key]
        }
    }

    @Mutation
    private SET_USAGE([asset, key, usage]: [AssetFile, string, AssetUsage?]) {
        if (usage !== undefined) {
            asset.usage[key] = usage
        } else {
            delete asset.usage[key]
        }
    }

    @Mutation
    private CLEAR_ASSETS(clear: boolean) {
        for (let j = 0; j < this.assets.length; j++){
            this.assets[j].selected = false
            Object.keys(this.assets[j].usage).forEach(key => {
                Vue.delete(this.assets[j].usage, key)
            })
            Object.keys(this.assets[j].pendingUsage).forEach(key => {
                Vue.delete(this.assets[j].pendingUsage, key)
            })
        }
    }

    @Mutation
    private CLEAR_VALID_ASSETS(clear: boolean) {
        for (let j = 0; j < this.validAssets.length; j++){
            this.validAssets[j].selected = false
            Object.keys(this.validAssets[j].usage).forEach(key => {
                Vue.delete(this.validAssets[j].usage, key)
            })
            Object.keys(this.validAssets[j].pendingUsage).forEach(key => {
                Vue.delete(this.validAssets[j].pendingUsage, key)
            })
        }
    }

    @Mutation
    private CLEAR_SELECTED_ASSETS(clear: boolean) {
        this.selectedAssets = []
    }

    @Mutation
    private DELETE_ASSETS(asset: AssetFile) {
        this.assets.splice(this.assets.indexOf(asset), 1);
        if(this.validAssets.some(a => a.assetId === asset.assetId)) {
            this.validAssets = this.validAssets.filter( a => a.assetId !== asset.assetId)
        }
    }

    @Action({ commit: 'SET_ASSETS' })
    public SetAssets(assets: AssetFile[]) {
        return assets
    }

    @Action({ commit: 'SELECT_ASSET' })
    public Select(asset?: AssetFile) {
        return asset
    }

    @Action({ commit: 'SET_URL' })
    public SetUrl(assetUrl: [string | number, string]) {
        return assetUrl
    }

    @Action({ commit: 'SET_ESTIMATED_PROGRESS' })
    public SetEstimatedProgress(assetProgress: [AssetFile, number]) {
        return assetProgress
    }

    @Action({ commit: 'SET_RECTANGLE' })
    public SetRectangle(assetRect: [AssetFile, Rect]) {
        return assetRect
    }

    @Action({ commit: 'CUSTOM_PUZZLE_SET_RECTANGLE' })
    public CustomPuzzleSetRectangle(assetRect: [AssetFile, Rect]) {
        return assetRect
    }

    @Action({ commit: 'SET_USAGE' })
    public SetUsage(usage: [AssetFile, string, AssetUsage?]) {
        return usage
    }

    @Action({ commit: 'SET_PENDING_USAGE' })
    public SetPendingUsage(pendingUsage: [AssetFile, string, AssetUsage?]) {
        return pendingUsage
    }

    @Action({ commit: 'CLEAR_ASSETS' })
    public ClearAssets(clear: boolean) {
        return clear
    }

    @Action({ commit: 'CLEAR_VALID_ASSETS' })
    public ClearValidAssets(clear: boolean) {
        return clear
    }

    @Action({ commit: 'CLEAR_SELECTED_ASSETS' })
    public ClearSelectedAssets(clear: boolean) {
        return clear
    }

    @Action({ commit: 'DELETE_ASSETS' })
    public DeleteAsset(asset: AssetFile) {
        return asset
    }

    get selected(): AssetFile | undefined {
        let asset: AssetFile | undefined = undefined
        this.assets.forEach(a => {
            if (a.selected) {
                asset = a
            }
        })
        return asset;
    }
}

export const AssetsModule = getModule(Assets)